import { type Reducer } from 'redux';
import type { DXGEmployee } from '@makemydeal/dr-platform-types';
import { EmployeeRole } from '@makemydeal/dr-platform-types';
import { type EmployeeDetailsState, type EmployeeDetails } from '@makemydeal/dr-dash-types';
import {
    EMPLOYEE_DETAILS_OPEN_SLIDEOUT,
    EMPLOYEE_DETAILS_CLOSE_SLIDEOUT,
    EMPLOYEE_DETAILS_UPDATE_EMPLOYEES,
    EMPLOYEE_DETAILS_START_UPDATE_REQUEST,
    EMPLOYEE_DETAILS_SEND_UPDATE_FAILURE,
    EMPLOYEE_DETAILS_SEND_UPDATE_SUCCESS,
    EMPLOYEE_SEARCH_REQUEST,
    EMPLOYEE_SEARCH_SUCCESS,
    EMPLOYEE_SEARCH_FAILURE
} from '../actionTypes/employeeDetailsActionTypes';
import { initActionTypes } from '@makemydeal/dr-shared-store';
import { normalizeEmployeeDetails } from '../utils/employeeDetailsUtils';

export type EmployeeDetailsReducer = Reducer<EmployeeDetailsState>;

const initialState: EmployeeDetailsState = {
    slideOutOpen: false,
    sendingUpdate: false,
    sendUpdateSuccess: false,
    employees: {
        [EmployeeRole.PrimarySalesperson]: undefined,
        [EmployeeRole.SecondarySalesperson]: undefined,
        [EmployeeRole.OtherSalesperson]: undefined,
        [EmployeeRole.SalesManager]: undefined,
        [EmployeeRole.AsstSalesManager]: undefined,
        [EmployeeRole.Closer]: undefined,
        [EmployeeRole.FIManager]: undefined,
        [EmployeeRole.CustomerService]: undefined,
        [EmployeeRole.AftermarketManager]: undefined,
        [EmployeeRole.DeliveryManager]: undefined,
        [EmployeeRole.Other]: undefined
    },
    searchResults: [],
    searchLoading: false,
    searchError: undefined
};

export const legacyEmployeeRoles = [
    EmployeeRole.PrimarySalesperson,
    EmployeeRole.SalesManager,
    EmployeeRole.FIManager,
    EmployeeRole.SecondarySalesperson
] as const;

export const allEmployeeRoles = [
    EmployeeRole.PrimarySalesperson,
    EmployeeRole.SecondarySalesperson,
    EmployeeRole.OtherSalesperson,
    EmployeeRole.SalesManager,
    EmployeeRole.AsstSalesManager,
    EmployeeRole.Closer,
    EmployeeRole.FIManager,
    EmployeeRole.CustomerService,
    EmployeeRole.AftermarketManager,
    EmployeeRole.DeliveryManager,
    EmployeeRole.Other
] as const;

export const reducer = (state = initialState, action: any) => {
    switch (action.type) {
        case EMPLOYEE_DETAILS_OPEN_SLIDEOUT: {
            return {
                ...state,
                slideOutOpen: true
            };
        }

        case EMPLOYEE_DETAILS_CLOSE_SLIDEOUT: {
            return {
                ...state,
                slideOutOpen: false,
                sendingUpdate: false
            };
        }

        case EMPLOYEE_DETAILS_UPDATE_EMPLOYEES: {
            return {
                ...state,
                employees: action.payload.employees
            };
        }

        case EMPLOYEE_DETAILS_START_UPDATE_REQUEST: {
            return {
                ...state,
                sendingUpdate: true
            };
        }

        case EMPLOYEE_DETAILS_SEND_UPDATE_FAILURE:
        case EMPLOYEE_DETAILS_SEND_UPDATE_SUCCESS: {
            const isSuccess = action.type === EMPLOYEE_DETAILS_SEND_UPDATE_SUCCESS;

            return {
                ...state,
                slideOutOpen: !isSuccess,
                sendingUpdate: false,
                sendUpdateSuccess: isSuccess,
                ...(isSuccess && { employees: action.payload.employees })
            };
        }

        case initActionTypes.INIT_SUCCESS: {
            const isAdditionalRolesEnabled = Boolean(
                action.payload.featureToggles?.enableAdditionalSalesRoles ??
                    action.payload.primaryOfferFeatureToggles?.enableAdditionalSalesRoles
            );
            const rolesToRetain: readonly EmployeeRole[] = isAdditionalRolesEnabled ? allEmployeeRoles : legacyEmployeeRoles;

            return {
                ...state,
                employees: Object.fromEntries(
                    action.payload.employees?.flatMap((employee: DXGEmployee) => {
                        if (!rolesToRetain.includes(employee.employeeRole)) {
                            // eslint-disable-next-line no-console
                            console.log('[employee-details]: dropped employee with unsupported role: ', employee.employeeRole);
                            return [];
                        }

                        const filteredEmployee: EmployeeDetails = normalizeEmployeeDetails(employee as unknown as EmployeeDetails);
                        return [[employee.employeeRole, filteredEmployee]];
                    }) || []
                )
            };
        }

        case EMPLOYEE_SEARCH_REQUEST: {
            return {
                ...state,
                searchLoading: true,
                searchError: undefined
            };
        }

        case EMPLOYEE_SEARCH_SUCCESS: {
            const responseData = action.payload;
            const employees = responseData?.data?.employees || responseData?.employees || [];
            return {
                ...state,
                searchResults: employees,
                searchLoading: false,
                searchError: undefined
            };
        }

        case EMPLOYEE_SEARCH_FAILURE: {
            return {
                ...state,
                searchResults: [],
                searchLoading: false,
                searchError: action.payload?.message || 'Search failed'
            };
        }
    }

    return state;
};
